import { useEffect, useState, type RefObject } from 'react';

/**
 * Tracks which of the given sections is currently in view — drives the active
 * underline on {@link LiveGroupAnchors}. One IntersectionObserver over the
 * section roots; the topmost intersecting section wins. `rootMargin` biases
 * the hit zone toward the upper part of the viewport so the highlighted chip
 * matches what the user reads, not what's at the fold.
 */
export function useActiveSection(sections: Array<{ id: string; ref: RefObject<HTMLElement | null> }>): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  // The observer effect re-runs only when the section id-set changes (hidden
  // groups appear/disappear), not on every render's array identity.
  const idsKey = sections.map((s) => s.id).join('|');

  useEffect(() => {
    const entriesVisible = new Map<Element, boolean>();
    const elements = sections.filter((s) => s.ref.current).map((s) => ({ id: s.id, el: s.ref.current! }));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entriesVisible.set(entry.target, entry.isIntersecting);
        const first = elements.find(({ el }) => entriesVisible.get(el));
        setActiveId(first ? first.id : null);
      },
      { rootMargin: '-96px 0px -55% 0px' },
    );
    for (const { el } of elements) observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsKey stands in for the sections array identity
  }, [idsKey]);

  return activeId;
}
